import { API_BASE_URL } from "../../constants/index"; // Base URL for the API
import HttpInterface from "../../helpers/HttpInterface"; // Helper class for making HTTP requests
import { CreateContract, UpdateContract } from "../../Models/Contract";
import { defaultFetchParam, FetchParam } from "../../Models/FetchParam"; // Fetch parameters model and default values
import { buildAPIUrl } from "../../utilities/api"; // Utility function to build the API URL with query parameters

export class ContractsService {
  private httpProxy: HttpInterface; // Instance of the HttpInterface to make HTTP requests

  constructor() {
    this.httpProxy = new HttpInterface(); // Creates an instance of the HttpInterface to handle requests
  }

  /**
   * Fetches all contracts from the contract service API.
   *
   * @param params The query parameters for the API request. Defaults to `defaultFetchParam` if not provided.
   * @returns A promise that resolves with the API response containing the list of contracts.
   */
  all(params: FetchParam = defaultFetchParam): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Builds the complete API URL using the base URL, endpoint, and parameters
    return this.httpProxy.fetch(
      buildAPIUrl(`${API_BASE_URL}/contract-service/contracts`, params),
      requestOptions
    );
  }

  /**
   * Fetches a specific contract by its ID from the API.
   *
   * @param id The unique identifier of the contract to fetch.
   * @returns A promise that resolves with the API response containing the contract details.
   */
  get(id: string): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Constructs the URL for fetching a specific contract by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/contract-service/contracts/${id}`,
      requestOptions
    );
  }

  /**
   * Creates a new contract in the contract service API.
   *
   * @param contract The details of the contract to create.
   * @returns A promise that resolves with the API response confirming the creation of the contract.
   */
  create(contract: CreateContract): Promise<any> {
    
    const requestOptions = {
      method: "POST", // HTTP method for the request
      body: JSON.stringify(contract), // Sends the contract data as JSON in the request body
    };

    // Sends a POST request to create the new contract
    return this.httpProxy.fetch(
      `${API_BASE_URL}/contract-service/contracts`,
      requestOptions
    );
  }

  /**
   * Updates an existing contract in the contract service API.
   *
   * @param contract The updated details of the contract.
   * @returns A promise that resolves with the API response confirming the update.
   */
  edit(contract: UpdateContract): Promise<any> {
    const requestOptions = {
      method: "PUT", // HTTP method for the request
      body: JSON.stringify(contract), // Sends the updated contract data as JSON in the request body
    };

    // Sends a PUT request to update the specific contract by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/contract-service/contracts/${contract.id}`,
      requestOptions
    );
  }

  /**
   * Deletes a contract from the contract service API.
   *
   * @param id The unique identifier of the contract to delete.
   * @returns A promise that resolves with the API response confirming the deletion.
   */
  delete(id: string): Promise<any> {
    const requestOptions = {
      method: "DELETE", // HTTP method for the request
    };

    // Sends a DELETE request to remove the specific contract by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/contract-service/contracts/${id}`,
      requestOptions
    );
  }
}
